import { InterceptorClient } from "../../interceptor/interceptorClient";
import {
  GET_GIFT_FINDER_PROMPTS_URL,
  GET_GIFT_FINDER_QUESTIONS_URL,
  GIFT_FINDER_ERROR_CODES,
  POST_GIFT_FINDER_SEARCH_URL,
} from "./constants";

const searchServerAPIUrl = process.env.NEXT_PUBLIC_SEARCH_SERVER_API_GATEWAY;
const isServer = typeof window === "undefined";

/** Server calls go direct to the search gateway; browser calls stay same-origin. */
const resolveBaseUrl = () => (isServer ? searchServerAPIUrl : "");

/**
 * @param {boolean} [isMobile]
 * @returns {Object}
 */
const giftFinderHeaders = (isMobile) => ({
  "Content-Type": "application/json",
  "x-device-type": isMobile ? "mobile" : "desktop",
  "x-domain": "www.fnp.com",
});

/**
 * @typedef {{ok: true, data: Object}} GiftFinderOk
 * @typedef {{ok: false, error: {code: string, message?: string, details?: string}}} GiftFinderErr
 */

/**
 * Build a failure result.
 * @param {string} code
 * @param {string} [message]
 * @param {string} [details]
 * @returns {GiftFinderErr}
 */
const toError = (code, message, details) => ({ ok: false, error: { code, message, details } });

/**
 * Normalize any gift-finder response into a discriminated result.
 *
 * This is mandatory on every call: `HttpClient._handleError` *resolves* with
 * `{ status, data }` rather than rejecting, so a 500 arrives looking like a success.
 * Callers must branch on `ok` and never rely on try/catch to detect failure.
 *
 * @param {unknown} raw - Whatever the interceptor resolved with.
 * @param {string} expectedKey - Key that must be present on a genuine success body.
 * @returns {GiftFinderOk|GiftFinderErr}
 */
export const normalizeGiftFinderResponse = (raw, expectedKey) => {
  if (!raw || typeof raw !== "object") {
    return toError(GIFT_FINDER_ERROR_CODES.NETWORK_ERROR);
  }

  // Contract error envelope, whether it arrived on a 200 or inside a swallowed error body.
  const envelope = raw.error?.code ? raw.error : raw.data?.error?.code ? raw.data.error : null;
  if (envelope || raw.success === false) {
    return toError(envelope?.code || GIFT_FINDER_ERROR_CODES.INTERNAL_ERROR, envelope?.message, envelope?.details);
  }

  // Swallowed HTTP failure: `{ status, data }` from the response interceptor.
  if (typeof raw.status === "number" && raw.status >= 400) {
    return toError(
      raw.status === 400 ? GIFT_FINDER_ERROR_CODES.VALIDATION_FAILED : GIFT_FINDER_ERROR_CODES.INTERNAL_ERROR,
    );
  }

  // Timeout/no-response resolves as `{status: undefined, data: undefined}`; mock-server mode
  // resolves as `{}`. Test the value, not just key presence — the timeout shape *has* a `data`
  // key whose value is undefined, so an `in` check alone would wave it through.
  if (raw[expectedKey] === undefined || raw[expectedKey] === null) {
    return toError(GIFT_FINDER_ERROR_CODES.NETWORK_ERROR);
  }

  return { ok: true, data: raw };
};

/**
 * Launch-rail prompt chips. Safe to fire-and-cache as soon as the entry point is visible.
 *
 * @param {Object} [options]
 * @param {boolean} [options.isMobile]
 * @param {string} [options.geoId] - Catalog of the page the finder was opened from, so the
 *   prompts match the country being browsed.
 * @returns {Promise<GiftFinderOk|GiftFinderErr>}
 */
export const getGiftFinderPromptsAPI = async ({ isMobile, geoId } = {}) => {
  try {
    const raw = await InterceptorClient.getGatewayInstance(resolveBaseUrl()).get(
      GET_GIFT_FINDER_PROMPTS_URL(geoId),
      false,
      null,
      giftFinderHeaders(isMobile),
    );
    return normalizeGiftFinderResponse(raw, "prompts");
  } catch (error) {
    return toError(GIFT_FINDER_ERROR_CODES.NETWORK_ERROR, error?.message);
  }
};

/**
 * Full chip/question config. Fetched once per session when the quiz opens — this is the
 * only source of chip vocabulary; it is never hardcoded or fallen back to client-side.
 *
 * @param {Object} [options]
 * @param {boolean} [options.isMobile]
 * @returns {Promise<GiftFinderOk|GiftFinderErr>}
 */
export const getGiftFinderQuestionsAPI = async ({ isMobile } = {}) => {
  try {
    const raw = await InterceptorClient.getGatewayInstance(resolveBaseUrl()).get(
      GET_GIFT_FINDER_QUESTIONS_URL(),
      false,
      null,
      giftFinderHeaders(isMobile),
    );
    return normalizeGiftFinderResponse(raw, "steps");
  } catch (error) {
    return toError(GIFT_FINDER_ERROR_CODES.NETWORK_ERROR, error?.message);
  }
};

/**
 * Submit quiz answers and get ranked results. Also used for pagination (same body,
 * incremented `page`) and for refine (edited answers, `page` reset to 0).
 *
 * @param {Object} body - See buildGiftFinderSearchBody.
 * @param {Object} [options]
 * @param {boolean} [options.isMobile]
 * @returns {Promise<GiftFinderOk|GiftFinderErr>}
 */
export const postGiftFinderSearchAPI = async (body, { isMobile } = {}) => {
  try {
    const raw = await InterceptorClient.getGatewayInstance(resolveBaseUrl()).post(
      body,
      POST_GIFT_FINDER_SEARCH_URL(),
      false,
      giftFinderHeaders(isMobile),
    );
    return normalizeGiftFinderResponse(raw, "data");
  } catch (error) {
    return toError(GIFT_FINDER_ERROR_CODES.NETWORK_ERROR, error?.message);
  }
};
